import { type PreferenceKey, type PreferenceValue, PREFERENCE_SCHEMAS, routes } from '@pmai/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { call } from './api/client';
import { queryKeyFor, useApiQuery } from './api/hooks';

/** Saves of one key go out one after another, so the last choice is the one stored. */
const saving = new Map<string, Promise<unknown>>();

/**
 * A remembered UI choice of the user in this workspace (FR-DASH-005, FR-TASK-014). `value` is
 * `undefined` while loading and `null` when never saved; `save` stores it on the server.
 */
export function usePreference<K extends PreferenceKey>(key: K) {
  const queryClient = useQueryClient();
  const opts = { params: { key } };
  const query = useApiQuery(routes.preferences.get, opts, { staleTime: Infinity });
  // The choice just made, shown in the same render (a controlled checkbox must not flip back
  // while the query cache notifies); ignored as soon as the cached data changes.
  const [justSaved, setJustSaved] = useState<{ value: PreferenceValue<K>; over: unknown } | null>(null);
  const parsed = query.data ? PREFERENCE_SCHEMAS[key].safeParse(query.data.value) : undefined;
  const stored: PreferenceValue<K> | null | undefined = query.data
    ? parsed?.success
      ? (parsed.data as PreferenceValue<K>)
      : null
    : undefined;
  const value = justSaved && justSaved.over === query.data ? justSaved.value : stored;
  const save = (next: PreferenceValue<K>) => {
    setJustSaved({ value: next, over: query.data });
    queryClient.setQueryData(queryKeyFor(routes.preferences.get, opts), { value: next });
    const previous = saving.get(key) ?? Promise.resolve();
    const request = previous
      .catch(() => undefined)
      .then(() => call(routes.preferences.put, { params: { key }, body: { value: next } }));
    saving.set(key, request);
    void request.catch(() => undefined);
  };
  return { value, loaded: !query.isPending, save };
}
